import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Text,
} from '@chakra-ui/react';
import { useDisclosure } from '@chakra-ui/react';
import { conversationAction, documentAction } from 'actions';
import { Skeleton } from 'components/ds';
import FilePreview from 'components/FilePreview';
import PreviewLoading from 'components/FilePreview/PreviewLoading';
import configs from 'configs';
import { HTTP_CODE_RESPONSE, TEMPORARY } from 'helpers/constant';
import { VIEW_MODE } from 'helpers/enum';
import { addTokenURL, getFileExtension } from 'helpers/file';
import { resolveDefaultModelId } from 'helpers/lib';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { UploadService } from 'services/upload/upload.service';
import { Citation, Conversation } from 'types/reducers/conversation';
import { getStorageReadBlobToken } from 'utils/blob';
import { v4 as uuidv4 } from 'uuid';

import Chat from './Chat';
import ConversationsList from './ConversationsList';

const Ask = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { conversations, selectedCitation, viewMode, processingStreamingChunkStatus } = useSelector(
    (state) => state.conversations
  );
  const models = useSelector((state: any) => state.model?.models || []);

  const [isLoading, setIsLoading] = useState(false);
  const [isFileValid, setIsFileValid] = useState(true);
  // True from the moment a citation is clicked until the preview panel is ready (covers the
  // read-token fetch + HEAD validation) so the user gets feedback instead of a dead click.
  const [isOpeningFile, setIsOpeningFile] = useState(false);

  const { isOpen, onOpen: onOpenSidebar, onClose: onCloseSidebar } = useDisclosure();

  const newChat = useMemo(
    () => ({
      id: uuidv4(),
      type: TEMPORARY,
      name: t('common.newChat'),
      last_updated: new Date().toISOString(),
    }),
    [t, i18n.language]
  );

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleOpenSidebarMobile = () => {
      onOpenSidebar();
    };

    window.addEventListener('open-sidebar-mobile', handleOpenSidebarMobile);

    return () => {
      window.removeEventListener('open-sidebar-mobile', handleOpenSidebarMobile);
    };
  }, [onOpenSidebar]);

  const loadData = () => {
    setIsLoading(true);
    dispatch(conversationAction.resetData());
    dispatch(conversationAction.getConversations());
    setIsLoading(false);
  };

  const onChangeConversation = (item: Conversation, resetModelTo?: string) => {
    if (processingStreamingChunkStatus) {
      return;
    }
    dispatch(conversationAction.updateViewMode(VIEW_MODE.CHAT));
    dispatch(
      conversationAction.updateCurrentConversation({
        conversation_id: item?.id,
        type: item?.type,
        selectedModelId: resetModelTo,
      })
    );
  };

  const addNewConversation = () => {
    if (processingStreamingChunkStatus) {
      return;
    }

    const defaultModelId = resolveDefaultModelId(models);

    const temporaryConversation = conversations.data.find(
      (conversation) => conversation.type === TEMPORARY
    );
    if (temporaryConversation) {
      // Reset the reused temp conversation to empty so the greeting + suggestions re-appear
      // on every New chat (the gate is now "no messages yet"). #77529-12
      dispatch(conversationAction.updateCurrentConversationData([]));
      dispatch(
        conversationAction.updateCurrentConversation({
          conversation_id: temporaryConversation.id,
          type: temporaryConversation?.type,
          selectedModelId: defaultModelId,
        })
      );
      return;
    }

    dispatch(conversationAction.addNewConversation(newChat));
    onChangeConversation(newChat, defaultModelId);
  };

  const checkReadTokenValid = (token: string | null) => {
    if (!token) {
      return false;
    }
    const searchParams = new URLSearchParams(token);
    const expiredTime = new Date(searchParams.get('se') as string).getTime();
    const currentTime = new Date().getTime();
    const offsetRefreshTime = configs.setting.TIMEOUT_REFRESH_SAS_TOKEN_IN_MS;

    return expiredTime > currentTime + offsetRefreshTime;
  };

  const onOpenFile = (citation: Citation) => {
    setIsOpeningFile(true);
    readFile(citation);
  };

  const readFile = (citation: Citation) => {
    if (!checkReadTokenValid(getStorageReadBlobToken())) {
      dispatch(documentAction.getReadToken());
    }
    fetchData(citation);
  };

  const fetchData = (citation: Citation) => {
    const fileUrl = addTokenURL(citation?.file_url, getStorageReadBlobToken());

    dispatch(
      conversationAction.updateSelectedCitation({
        ...citation,
        file_url: fileUrl,
        fileType: getFileExtension(citation.file_url),
        page: 1,
      })
    );

    (async () => {
      try {
        const res = await UploadService.fetchHeadFile(fileUrl);
        if (
          res.statusText !== 'OK' &&
          (res.status === HTTP_CODE_RESPONSE.UNAUTHORIZED ||
            res.status === HTTP_CODE_RESPONSE.NO_SAS_TOKEN)
        ) {
          readFile(citation);
        } else if (res.status === HTTP_CODE_RESPONSE.NOT_FOUND) {
          setIsFileValid(false);
          setIsOpeningFile(false);
          dispatch(conversationAction.updateViewMode(VIEW_MODE.PREVIEW_FILE));
        } else {
          setIsFileValid(true);
          setIsOpeningFile(false);
          dispatch(conversationAction.updateViewMode(VIEW_MODE.PREVIEW_FILE));
        }
      } catch (error) {
        setIsOpeningFile(false);
        console.log(error);
      }
    })();
  };

  return (
    <>
      <Flex gap={4} w={{ base: '100%', md: 'auto' }} h='100%' minH={0} bg='bg.canvas'>
        {isLoading ? (
          /* DS loading skeleton — mirrors panel + thread layout (design-guidelines.md §4) */
          <Flex gap={4} w='100%' h='100%' minH={0} p={2}>
            <Box flexShrink={0} display={{ base: 'none', md: 'block' }} w='300px'>
              <Skeleton h='40px' borderRadius='md' mb={4} />
              <Skeleton h='56px' borderRadius='md' mb={2} />
              <Skeleton h='56px' borderRadius='md' mb={2} />
              <Skeleton h='56px' borderRadius='md' />
            </Box>
            <Box flex={1}>
              <Skeleton h='72px' borderRadius='lg' mb={4} maxW='560px' />
              <Skeleton h='120px' borderRadius='lg' maxW='680px' />
            </Box>
          </Flex>
        ) : (
          <>
            <Box display={{ base: 'none', md: 'block' }} h='100%'>
              <ConversationsList
                onAddNew={addNewConversation}
                onChange={onChangeConversation}
                onCloseSidebar={onCloseSidebar}
              />
            </Box>
            <Flex pos={'relative'} justify={'center'} overflow='hidden' w='100%' h='100%' minH={0}>
              <Chat onOpenFile={onOpenFile} onAddNew={addNewConversation} />
              {(isOpeningFile ||
                (viewMode === VIEW_MODE.PREVIEW_FILE && selectedCitation?.file_url)) && (
                <Box
                  pos={{ base: 'absolute', md: 'static' }}
                  zIndex={{ base: 9999, md: 'initial' }}
                  top={{ base: 0, md: 'initial' }}
                  right={{ base: 0, md: 'initial' }}
                  bottom={{ base: 0, md: 'initial' }}
                  left={{ base: -10, md: 'initial' }}
                  w={{ base: '100vw', md: '50%' }}
                >
                  {isOpeningFile ? (
                    <Box
                      overflow='hidden'
                      w='100%'
                      h='100%'
                      ms='16px'
                      bg='bg_main_container'
                      borderRadius='20px'
                      shadow='shadow_box'
                    >
                      <PreviewLoading />
                    </Box>
                  ) : (
                    <FilePreview
                      onClose={() => dispatch(conversationAction.updateViewMode(VIEW_MODE.CHAT))}
                      data={selectedCitation}
                      isValid={isFileValid}
                    />
                  )}
                </Box>
              )}
            </Flex>
            <Drawer isOpen={isOpen} onClose={onCloseSidebar} placement='left'>
              <DrawerOverlay bg='blackAlpha.500' backdropFilter='blur(3px)' />
              <DrawerContent bg='bg.sidebar'>
                <DrawerCloseButton color='text.muted' />
                <DrawerHeader
                  color='text.default'
                  fontFamily='heading'
                  fontSize='lg'
                  fontWeight={600}
                  borderColor='border.default'
                  borderBottomWidth='1px'
                >
                  <Text>{t('chat.conversations')}</Text>
                </DrawerHeader>

                <DrawerBody px={2} py={3}>
                  <Box>
                    <ConversationsList
                      onAddNew={addNewConversation}
                      onChange={onChangeConversation}
                      onCloseSidebar={onCloseSidebar}
                    />
                  </Box>
                </DrawerBody>
              </DrawerContent>
            </Drawer>
          </>
        )}
      </Flex>
    </>
  );
};

export default Ask;
